import { MouseEvent, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { push, useRouteMatch } from '../../app/route';
import { Tenant, TenantProvider } from '../../types';
import {
  selectCurrentTenantId,
} from '../user/userSlice';
import {
  getTenantAction,
  selectGetTenantResult,
  getTenantDetailUrl,
} from './tenantSlice';
import { TenantProviderModal, TenantProviderModalProps } from './TenantProviderModal';

type PathParams = {
  tenant_id: string;
};

type ProviderModalProps = Pick<TenantProviderModalProps, 'show'|'init'>;

export function TenantViewPage() {
  const dispatch = useAppDispatch();
  const match = useRouteMatch<PathParams>();
  const get_result = useAppSelector(selectGetTenantResult);
  const tenant_id = useAppSelector(selectCurrentTenantId);
  const [tenant, setTenant] = useState<Tenant|undefined>();
  const [provider_modal_props, setProviderModalProps] = useState<ProviderModalProps>({ show: false });

  useEffect(() => {
    if (tenant_id !== match.params.tenant_id) {
      dispatch(push(getTenantDetailUrl(tenant_id, 'view')));
    }
  }, [tenant_id, dispatch, match.params.tenant_id]);

  useEffect(() => {
    dispatch(getTenantAction({ tenant_id }));
  }, [tenant_id, dispatch]);

  useEffect(() => {
    if (get_result) {
      if (getTenantAction.fulfilled.match(get_result)) {
        setTenant(get_result.payload);
      }
    }
  }, [get_result]);

  const handleGoEdit = () => {
    dispatch(push(getTenantDetailUrl(tenant_id, 'edit')));
  };

  const handleShowProviderModal = (e: MouseEvent, provider: TenantProvider) => {
    e.preventDefault();
    setProviderModalProps({ show: true, init: provider });
  };

  const handleCloseProviderModal = () => {
    setProviderModalProps({ show: false });
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between py-4">
        <h1>Tenant Info</h1>
        <button className="btn btn-primary my-2" onClick={handleGoEdit}>
          Edit
        </button>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">TenantId</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{tenant?.tenant_id}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Title</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{tenant?.title}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Name</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{tenant?.name}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Description</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{tenant?.description}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">FQDN</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{tenant?.fqdn}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Username Can Login</label>
        <div className="mt-2 col-sm-6">
          <div className="col-sm-6">
            <input type="checkbox" readOnly className="form-check-input"
              checked={tenant?.username.can_login ?? false}
            />
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Username Can Singup</label>
        <div className="mt-2 col-sm-6">
          <div className="col-sm-6">
            <input type="checkbox" readOnly className="form-check-input"
              checked={tenant?.username.can_signup ?? false}
            />
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="state" className="col-sm-3 col-form-label">Providers</label>
        <div className="mt-2 col-sm-6">
          {tenant?.providers.map(provider => (
            <a key={provider.name} className="me-1" href="/#" role="button" onClick={(e) => handleShowProviderModal(e, provider)}>
              <span className="badge bg-success px-2 py-1">{provider.name}</span>
            </a>
          ))}
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="state" className="col-sm-3 col-form-label">Custom Domains</label>
        <div className="mt-2 col-sm-6">
          {tenant?.custom_domains.map(custom_domain => (
            <span key={custom_domain.fqdn} className="badge bg-success px-2 py-1 me-1">{custom_domain.fqdn}</span>
          ))}
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="signup_role_id" className="col-sm-3 col-form-label">Sign Up RoleId</label>
        <div className="col-sm-6">
          <span id="signup_role_id" className="form-control-plaintext">{tenant?.signup_role_id}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Auto Link Email</label>
        <div className="mt-2 col-sm-6">
          <div className="col-sm-6">
            <input type="checkbox" readOnly className="form-check-input"
              checked={tenant?.auto_link_email ?? false}
            />
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="tenant_state" className="col-sm-3 col-form-label">State</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{tenant?.state}</span>
        </div>
      </div>
      <TenantProviderModal
        {...provider_modal_props}
        mode="view"
        onClose={handleCloseProviderModal}/>
    </div>
  );
}
